'use client'

import { ExplanationPageDto } from '@/generated/graphql'
import type { ReactNode } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { VideoPlayer } from '@/VideoPlayer'
import { AccountTable } from '@/Account/Table'
import { Button } from '@/General/Button'
import { InfoAlert } from '@/General/InfoAlert'
import { ExplanationFreeDownloadButton } from '@/Explanation/ExplanationFreeDownloadButton'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { PlayIcon } from '@heroicons/react/24/solid'

type ExplanationVideoContentProps = {
  explanationPage: ExplanationPageDto
  selectedFileIndex?: number | null
  productName?: string | null
  iosInstallLink?: string | null
  androidInstallLink?: string | null
  accessCode?: string | null
  accessCodeAlreadyRedeemed?: boolean | null
}

const AppInstallAlert = ({
  productName,
  iosInstallLink,
  androidInstallLink,
  addToAccountButton,
}: {
  productName?: string | null
  iosInstallLink?: string | null
  androidInstallLink?: string | null
  addToAccountButton?: ReactNode
}) => {
  if (!iosInstallLink && !androidInstallLink && !addToAccountButton) return null
  const canAddToAccount = Boolean(addToAccountButton)

  return (
    <InfoAlert
      variant={canAddToAccount ? 'success' : 'warning'}
      title={
        canAddToAccount
          ? `Add ${productName ?? 'this app'} to your Vanishing Inc. account`
          : `Install ${productName ?? 'this app'} on your device`
      }
      className="mt-6 mb-6"
    >
      {canAddToAccount ? (
        <p>
          This app uses your Vanishing Inc. account to verify access. Add it to
          your account before logging in to the app.
        </p>
      ) : (
        <p>Download it from the relevant app store using the links below:</p>
      )}
      {addToAccountButton && (
        <div className="mt-4 flex flex-wrap gap-3">{addToAccountButton}</div>
      )}
      {(iosInstallLink || androidInstallLink) && (
        <>
          {canAddToAccount && (
            <p className="mt-4">Then install it from the relevant app store:</p>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            {iosInstallLink && (
              <a
                href={iosInstallLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 transition"
              >
                Download for iOS
              </a>
            )}
            {androidInstallLink && (
              <a
                href={androidInstallLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-green-700 transition"
              >
                Download for Android
              </a>
            )}
          </div>
        </>
      )}
    </InfoAlert>
  )
}

type ExplanationFile = ExplanationPageDto['downloadFiles'][number]
type FileKind = 'video' | 'ebook' | 'other' | 'missing'
type ExplanationFileListItem = {
  file: ExplanationFile
  index: number
}

const getFreeDownloadSlug = (downloadUrl: string | null): string | null => {
  if (!downloadUrl) return null
  const match = downloadUrl.match(/\/account\/free\/([^/?#]+)/i)
  return match?.[1] ?? null
}

const getFileKind = (
  file: Pick<ExplanationFile, 'fileType' | 'filePath'>
): FileKind => {
  if (!file.filePath) return 'missing'

  const type = file.fileType?.trim().toUpperCase() ?? ''

  if (type === 'VIDEO') return 'video'
  if (type === 'EBOOK') return 'ebook'

  return file.filePath ? 'other' : 'missing'
}

const getFileTypeLabel = (file: ExplanationFile) => {
  const type = file.fileType?.trim().toUpperCase() ?? ''
  if (type === 'VIDEO') return 'Video'
  if (type === 'EBOOK') return 'Ebook'
  return file.fileType ?? ''
}

const ExplanationFilesTable = ({
  files,
  selectedIndex,
  getHref,
  onRowNavigate,
  kind,
}: {
  files: ExplanationFileListItem[]
  selectedIndex: number
  getHref: (index: number) => string
  onRowNavigate: (href: string) => void
  kind: 'videos' | 'files'
}) => {
  return (
    <AccountTable headers={[]}>
      {files.map(({ file, index }) => {
        const isCurrent = index === selectedIndex
        const title = file.title ?? `Part ${index + 1}`
        const typeLabel = getFileTypeLabel(file)
        const href = getHref(index)

        const goToRow = () => {
          if (isCurrent) return
          onRowNavigate(href)
        }

        return (
          <tr
            key={file.id}
            className={`hover:bg-gray-50 ${isCurrent ? '' : 'cursor-pointer'}`}
            onClick={goToRow}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                goToRow()
              }
            }}
            tabIndex={isCurrent ? -1 : 0}
            role={isCurrent ? undefined : 'link'}
            aria-current={isCurrent ? 'true' : undefined}
          >
            <td className="px-4 py-3 text-gray-900 w-5/8">
              <div className="font-medium line-clamp-1 pr-3">
                {title}
                {isCurrent && (
                  <span className="text-gray-500 font-normal">
                    {' '}
                    (currently selected)
                  </span>
                )}
              </div>
              {typeLabel && (
                <div className="text-sm text-gray-500 mt-1">{typeLabel}</div>
              )}
            </td>
            <td className="px-4 py-4 text-right">
              <div className="flex items-center justify-end gap-2">
                {kind === 'videos' ? (
                  <Button
                    color="green"
                    size="sm"
                    href={href}
                    disabled={isCurrent}
                  >
                    <PlayIcon className="w-4 h-4" />
                    <span className="hidden md:block">
                      {isCurrent ? 'Playing' : 'Play'}
                    </span>
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    color="blue"
                    href={href}
                    disabled={isCurrent}
                  >
                    <span className="hidden md:block">
                      {isCurrent ? 'Viewing' : 'View'}
                    </span>
                    <ChevronRightIcon
                      className="w-4 h-4 md:hidden"
                      strokeWidth={3}
                    />
                  </Button>
                )}
              </div>
            </td>
          </tr>
        )
      })}
    </AccountTable>
  )
}

export default function ExplanationVideoContent({
  explanationPage,
  selectedFileIndex,
  productName,
  iosInstallLink,
  androidInstallLink,
  accessCode,
  accessCodeAlreadyRedeemed,
}: ExplanationVideoContentProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const files = [...explanationPage.downloadFiles].sort((a, b) => {
    const positionA = a.position ?? Number.MAX_SAFE_INTEGER
    const positionB = b.position ?? Number.MAX_SAFE_INTEGER
    return positionA - positionB
  })

  const hasMultipleFiles = files.length > 1
  const selectedIndex =
    typeof selectedFileIndex === 'number' &&
    Number.isFinite(selectedFileIndex) &&
    selectedFileIndex >= 0 &&
    selectedFileIndex < files.length
      ? selectedFileIndex
      : 0

  const currentFile = files[selectedIndex] ?? null
  const currentKind = currentFile ? getFileKind(currentFile) : 'missing'
  const indexedFiles = files.map((file, index) => ({ file, index }))
  const videoFiles = indexedFiles.filter(
    ({ file }) => getFileKind(file) === 'video'
  )
  const resourceFiles = indexedFiles.filter(
    ({ file }) => getFileKind(file) !== 'video'
  )

  const fileUrl = currentFile?.filePath ?? null
  const downloadUrl = explanationPage.downloadUrl ?? null
  const freeDownloadSlug = getFreeDownloadSlug(downloadUrl)
  const hasAppInstallLinks = Boolean(iosInstallLink || androidInstallLink)
  const hasAppAccountGrant = hasAppInstallLinks && Boolean(downloadUrl)
  const appGrantProductSlug = hasAppAccountGrant
    ? (explanationPage.urlSlug ?? freeDownloadSlug)
    : null
  const downloadGrantProductSlug = hasAppInstallLinks ? null : freeDownloadSlug
  const shouldShowAccountGrantButton = accessCodeAlreadyRedeemed !== true
  const currentPath = pathname ?? '/explanation/'
  const redirectPath = searchParams?.toString()
    ? `${currentPath}?${searchParams.toString()}`
    : currentPath

  const introHtmlRaw =
    explanationPage.meta.find(m => m.key === 'exp-intro')?.value ?? null

  const introHtml = introHtmlRaw || null

  const buildFileHref = (index: number) => {
    const nextParams = new URLSearchParams(searchParams?.toString() ?? '')
    nextParams.set('file', String(index))
    const query = nextParams.toString()
    return query ? `${pathname}?${query}` : pathname
  }

  return (
    <div className="w-full max-w-6xl mx-auto px-6 py-10">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-3xl md:text-4xl font-semibold">
          {explanationPage.name ?? 'Explanation'}
        </h1>
        {downloadUrl && !hasAppInstallLinks && (
          <>
            {downloadGrantProductSlug && shouldShowAccountGrantButton ? (
              <ExplanationFreeDownloadButton
                productSlug={downloadGrantProductSlug}
                redirectPath={redirectPath}
                accessCode={accessCode}
                productName={productName ?? explanationPage.name}
              />
            ) : (
              <a
                href={downloadUrl}
                download
                className="inline-flex w-full md:w-auto items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white bg-[#198754] border border-[#198754] shadow transition hover:brightness-95"
              >
                Download this video
              </a>
            )}
          </>
        )}
      </div>

      {hasAppInstallLinks && (
        <AppInstallAlert
          productName={productName ?? explanationPage.name}
          iosInstallLink={iosInstallLink}
          androidInstallLink={androidInstallLink}
          addToAccountButton={
            appGrantProductSlug && shouldShowAccountGrantButton ? (
              <ExplanationFreeDownloadButton
                productSlug={appGrantProductSlug}
                redirectPath={redirectPath}
                kind="app"
                className="w-full sm:w-auto"
                accessCode={accessCode}
                productName={productName ?? explanationPage.name}
              />
            ) : null
          }
        />
      )}

      {introHtml && (
        <div
          className="prose max-w-none mt-4"
          dangerouslySetInnerHTML={{ __html: introHtml }}
        />
      )}

      <div className="mt-6">
        {currentKind === 'video' && fileUrl ? (
          <VideoPlayer
            url={fileUrl}
            name={currentFile?.title ?? explanationPage.name ?? undefined}
            chaptersVtt={currentFile?.vtt ?? undefined}
          />
        ) : currentKind === 'video' ? (
          <div className="w-full rounded-lg bg-gray-200 p-8 text-center text-gray-700">
            Video available soon.
          </div>
        ) : currentKind === 'ebook' && fileUrl ? (
          <div className="w-full rounded-lg border border-gray-200 bg-white p-6 text-center">
            <p className="text-lg font-semibold text-gray-800 mb-4">
              This explanation is delivered as an ebook download.
            </p>
            <a
              href={fileUrl ?? undefined}
              className="inline-flex items-center justify-center rounded-md bg-green-700 px-5 py-2 text-white font-semibold hover:bg-green-800 transition"
              download
            >
              Download ebook
            </a>
          </div>
        ) : currentKind === 'other' && fileUrl ? (
          <div className="w-full rounded-lg border border-gray-200 bg-white p-6 text-center">
            <p className="text-lg font-semibold text-gray-800 mb-2">
              This file is ready to download.
            </p>
            <p className="text-sm text-gray-600">
              <a
                className="underline hover:no-underline"
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open file
              </a>
            </p>
          </div>
        ) : (
          <div className="w-full rounded-lg bg-gray-200 p-8 text-center text-gray-700">
            Explanation file available soon.
          </div>
        )}
      </div>

      {hasMultipleFiles && videoFiles.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Videos</h2>
          <ExplanationFilesTable
            files={videoFiles}
            selectedIndex={selectedIndex}
            getHref={buildFileHref}
            onRowNavigate={href => router.push(href)}
            kind="videos"
          />
        </div>
      )}

      {hasMultipleFiles && resourceFiles.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Files</h2>
          <ExplanationFilesTable
            files={resourceFiles}
            selectedIndex={selectedIndex}
            getHref={buildFileHref}
            onRowNavigate={href => router.push(href)}
            kind="files"
          />
        </div>
      )}
    </div>
  )
}
